import { logger } from './logger';
import api from './api';

/**
 * Visibility API utilities
 * Uses managed API client for automatic JWT token handling, proactive token refresh,
 * and consistent error handling
 */

export interface VisibilitySettings {
  visibilitySettings: Array<{
    artworkId: string;
    visibility: 'public' | 'private';
  }>;
}

export interface VisibilityUpdateResponse {
  message: string;
  artworkId: string;
  visibility: 'public' | 'private';
}

export interface PublicCatalogResponse {
  artwork: Array<Record<string, unknown>>;
  count: number;
}

export interface UserPublicCatalogResponse extends PublicCatalogResponse {
  ownerDisplayName: string | null;
  ownerAliId: string | null;
  /** True only when the owner has published a public CV for this catalog scope (GH#1228). */
  hasPublicCV?: boolean;
}

/**
 * Update visibility for a single artwork
 * Uses managed API client for automatic JWT token handling
 */
export const updateArtworkVisibility = async (
  artworkId: string,
  visibility: 'public' | 'private'
): Promise<VisibilityUpdateResponse> => {
  try {
    const response = await api.put('/api/visibility', {
      artworkId,
      visibility
    });
    return response.data;
  } catch (error) {
    logger.error('[Visibility API] Failed to update visibility', error);
    throw error;
  }
};

/**
 * Fetch all visibility settings for current user
 */
export const fetchVisibilitySettings = async (): Promise<VisibilitySettings> => {
  try {
    const response = await api.get('/api/visibility');
    return response.data;
  } catch (error) {
    logger.error('[Visibility API] Failed to fetch settings', error);
    throw error;
  }
};

/**
 * Get visibility for a single item
 */
export const getItemVisibility = async (
  artworkId: string
): Promise<'public' | 'private'> => {
  try {
    const response = await api.get(`/api/visibility/${artworkId}`);
    return response.data.visibility;
  } catch (error) {
    logger.error('[Visibility API] Failed to get item visibility', error);
    throw error;
  }
};

/**
 * Update single item visibility
 */
export const updateItemVisibility = async (
  artworkId: string,
  visibility: 'public' | 'private'
): Promise<{ success: boolean }> => {
  try {
    const response = await api.put(`/api/visibility/${artworkId}`, {
      visibility
    });
    return response.data;
  } catch (error) {
    logger.error('[Visibility API] Failed to update item visibility', error);
    throw error;
  }
};

export interface BulkVisibilityResponse {
  message: string;
  updated: number;
  failed: number;
  visibility: 'public' | 'private';
}

/**
 * Update visibility for multiple artworks at once
 */
export const updateVisibilityBulk = async (
  artworkIds: string[],
  visibility: 'public' | 'private',
  ownerId?: string | null
): Promise<BulkVisibilityResponse> => {
  try {
    const params = ownerId ? `?owner_id=${ownerId}` : '';
    const response = await api.put(`/api/visibility/bulk${params}`, {
      artworkIds,
      visibility
    });
    return response.data;
  } catch (error) {
    logger.error('[Visibility API] Failed to bulk update visibility', error);
    throw error;
  }
};

/**
 * Fetch public catalog items (no auth required)
 * Note: Uses managed client for consistency, even though it's a public endpoint
 */
export const fetchPublicCatalog = async (userId?: string): Promise<PublicCatalogResponse> => {
  try {
    const url = userId
      ? `/api/catalog/public?user_id=${userId}`
      : '/api/catalog/public';
    const response = await api.get(url);
    return response.data;
  } catch (error) {
    logger.error('[Visibility API] Failed to fetch public catalog', error);
    throw error;
  }
};

/**
 * Fetch public catalog items for a specific user by ALI ID (no auth required)
 */
export const fetchUserPublicCatalog = async (aliId: string): Promise<UserPublicCatalogResponse> => {
  const response = await api.get(`/api/catalog/public/user/${encodeURIComponent(aliId)}`);
  return response.data;
};
